import axios from "axios";
import { BASE_URL } from "./utils";

const SURVEY_API_URL = `${BASE_URL}/surveys`;

const authHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const createSurvey = async (organisationId, surveyData, token) => {
    try {
        const response = await axios.post(
            `${SURVEY_API_URL}/create`,
            { organisationId, surveyData },
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateSurvey = async (surveyId, surveyData, organisationId, token) => {
    try {
        const response = await axios.post(
            `${SURVEY_API_URL}/update`,
            { surveyId, surveyData, organisationId },
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteSurvey = async (surveyId, organisationId, token) => {
    try {
        const response = await axios.post(
            `${SURVEY_API_URL}/delete`,
            { surveyId, organisationId },
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const fetchSurveys = async (organisationId, token) => {
    try {
        const response = await axios.get(`${SURVEY_API_URL}/fetch`, {
            params: { organisationId },
            ...authHeader(token),
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getSurvey = async (surveyId, { preview = false, token } = {}) => {
    try {
        const config = {};
        if (preview) config.params = { preview: 1 };
        if (token) config.headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${SURVEY_API_URL}/${surveyId}`, config);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const duplicateSurvey = async (surveyId, organisationId, token) => {
    try {
        const response = await axios.post(
            `${SURVEY_API_URL}/duplicate`,
            { surveyId, organisationId },
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateSurveyStatus = async (surveyId, status, organisationId, token) => {
    try {
        const response = await axios.post(
            `${SURVEY_API_URL}/update-status`,
            { surveyId, status, organisationId },
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const trackVisit = async (surveyId, memberId) => {
    try {
        const response = await axios.post(`${SURVEY_API_URL}/track-visit`, {
            surveyId,
            memberId: memberId || null,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const startResponse = async (surveyId, memberId, respondentInfo) => {
    try {
        const response = await axios.post(`${SURVEY_API_URL}/start-response`, {
            surveyId,
            memberId: memberId || null,
            respondentInfo,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const saveProgress = async (surveyId, responseId, answers) => {
    try {
        const response = await axios.post(`${SURVEY_API_URL}/save-progress`, {
            surveyId,
            responseId,
            answers,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const resumeResponse = async (surveyId, responseId) => {
    try {
        const response = await axios.get(
            `${SURVEY_API_URL}/${surveyId}/resume/${responseId}`
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const submitResponse = async ({
    surveyId,
    responseId,
    memberId,
    answers,
    respondentInfo,
}) => {
    try {
        const response = await axios.post(`${SURVEY_API_URL}/submit-response`, {
            surveyId,
            responseId: responseId || null,
            memberId: memberId || null,
            answers,
            respondentInfo,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getSurveyAnalytics = async (surveyId, token) => {
    try {
        const response = await axios.get(
            `${SURVEY_API_URL}/${surveyId}/analytics`,
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getSurveyResponses = async (surveyId, token) => {
    try {
        const response = await axios.get(
            `${SURVEY_API_URL}/${surveyId}/responses`,
            authHeader(token)
        );
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getPublicSummary = async (surveyId) => {
    try {
        const response = await axios.get(`${SURVEY_API_URL}/${surveyId}/public-summary`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// Returns a download URL the browser can hit directly with the auth token
// embedded as a query string is NOT possible because the route uses Bearer auth.
// Instead we fetch as a blob and trigger a download client-side.
const downloadAuthed = async (url, token, suggestedName) => {
    const response = await axios.get(url, {
        ...authHeader(token),
        responseType: "blob",
    });
    const blob = new Blob([response.data], {
        type: response.headers["content-type"],
    });
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = suggestedName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
};

export const exportResponsesCSV = async (surveyId, surveyTitle, token) => {
    const safeName = (surveyTitle || "survey").replace(/[^\w-]+/g, "_").slice(0, 80);
    await downloadAuthed(
        `${SURVEY_API_URL}/${surveyId}/export.csv`,
        token,
        `${safeName}_responses.csv`
    );
};

export const exportResponsesXLSX = async (surveyId, surveyTitle, token) => {
    const safeName = (surveyTitle || "survey").replace(/[^\w-]+/g, "_").slice(0, 80);
    await downloadAuthed(
        `${SURVEY_API_URL}/${surveyId}/export.xlsx`,
        token,
        `${safeName}_responses.xlsx`
    );
};
